'use client';

import { ReactNode, useEffect } from 'react';
import { MinimalErrorBoundary } from '@/shared/components/error/ErrorBoundary';
import { COMPANY } from '@voley/shared-types/constants';
import { lightTheme, cssVariableMap } from '../../styles/theme';

function AuthLayoutContent({ children }: { children: ReactNode }) {
  // Apply light theme CSS variables for auth pages
  useEffect(() => {
    Object.entries(cssVariableMap).forEach(([cssVar, themeKey]) => {
      document.documentElement.style.setProperty(
        cssVar,
        lightTheme[themeKey as keyof typeof lightTheme] as string
      );
    });

    Object.entries(lightTheme.tagColors).forEach(([colorName, colorValue]) => {
      document.documentElement.style.setProperty(
        `--color-tag-${colorName}`,
        colorValue
      );
    });

    document.documentElement.style.setProperty('--color-background', lightTheme.background);
    document.documentElement.style.setProperty('--color-surface', lightTheme.cardBackground);
    document.documentElement.style.setProperty('--color-primary', lightTheme.primary);
    document.documentElement.style.setProperty('--color-border', lightTheme.border);
    document.documentElement.style.setProperty('--color-text-primary', lightTheme.textPrimary);
    document.documentElement.style.setProperty('--color-text-secondary', lightTheme.textSecondary);
    document.documentElement.style.setProperty('--color-card-bg', lightTheme.cardBackground);
    document.documentElement.style.setProperty('--color-input-bg', lightTheme.inputBackground);
    document.documentElement.style.setProperty('--color-hover-bg', lightTheme.hoverBackground);
  }, []);

  // System font stack matching external pages
  const systemFont = '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

  // Centered card layout for auth pages
  return (
    <div 
      className="min-h-screen flex flex-col items-center justify-center py-12 px-4 sm:px-6 lg:px-8"
      style={{ backgroundColor: '#ffffff', fontFamily: systemFont }}
    >
      {/* Subtle gradient background */}
      <div 
        className="fixed inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at top right, rgba(53, 129, 207, 0.04) 0%, transparent 50%), radial-gradient(ellipse at bottom left, rgba(53, 129, 207, 0.03) 0%, transparent 50%)',
        }}
      />
      
      {/* Main content container - smaller for login */}
      <div 
        className="auth-layout w-full max-w-md bg-white rounded-2xl shadow-sm border border-gray-100 relative z-10 p-8"
      >
        {children}
      </div>
      
      {/* Footer */}
      <div className="mt-8 text-center relative z-10">
        <p className="text-sm text-gray-400">
          {COMPANY.copyrightNotice}
        </p>
      </div>
    </div>
  );
}

export default function AuthLayout({ children }: { children: ReactNode }) {
  return (
    <MinimalErrorBoundary>
      <AuthLayoutContent>
        {children}
      </AuthLayoutContent>
    </MinimalErrorBoundary>
  );
}
